/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */

import { atom } from 'jotai'

import { basketAtom, initialBasketState } from '@/store/basket/atoms'
import type { AddItemPayload, BasketItem, ItemIdentifierPayload, UpdateQuantityPayload } from '@/store/basket/types'

/**
 * Atom to add an item to the basket.
 * If the item (product + variant) already exists, its quantity is increased.
 */
const addItemAtom = atom(null, (get, set, payload: AddItemPayload) => {
  const currentBasket = get(basketAtom)
  const quantityToAdd = payload.quantity ?? 1 // Default to adding 1

  if (quantityToAdd <= 0) return // Don't add zero or negative quantity

  const existingItemIndex = currentBasket.items.findIndex(
    (item) =>
      item.productId === payload.productId &&
      item.variantId === payload.variantId &&
      item.productType === payload.productType,
  )

  let newItems: Array<BasketItem>

  if (existingItemIndex > -1) {
    // Item exists, update quantity
    newItems = currentBasket.items.map((item, index) =>
      index === existingItemIndex ? { ...item, quantity: item.quantity + quantityToAdd } : item,
    )
  } else {
    // Item doesn't exist, add it
    const newItem: BasketItem = {
      productId: payload.productId,
      productType: payload.productType,
      variantId: payload.variantId,
      quantity: quantityToAdd,
    }
    newItems = [...currentBasket.items, newItem]
  }

  set(basketAtom, { items: newItems })
})

/**
 * Atom to remove an item from the basket completely.
 */
const removeItemAtom = atom(null, (get, set, payload: ItemIdentifierPayload) => {
  const currentBasket = get(basketAtom)
  const newItems = currentBasket.items.filter(
    (item) => !(item.productId === payload.productId && item.variantId === payload.variantId),
  )

  // Only update state if items actually changed
  if (newItems.length !== currentBasket.items.length) {
    set(basketAtom, { items: newItems })
  }
})

/**
 * Atom to update the quantity of a specific item in the basket.
 * If the quantity is set to 0 or less, the item is removed.
 */
const updateItemQuantityAtom = atom(null, (get, set, payload: UpdateQuantityPayload) => {
  const currentBasket = get(basketAtom)

  if (payload.quantity <= 0) {
    // Remove item if quantity is zero or negative
    const newItems = currentBasket.items.filter(
      (item) => !(item.productId === payload.productId && item.variantId === payload.variantId),
    )

    // Only update state if items actually changed
    if (newItems.length !== currentBasket.items.length) {
      set(basketAtom, { items: newItems })
    }
  } else {
    // Update quantity if item exists and quantity > 0
    let itemFound = false
    const newItems = currentBasket.items.map((item) => {
      if (item.productId === payload.productId && item.variantId === payload.variantId) {
        itemFound = true
        return { ...item, quantity: payload.quantity }
      }
      return item
    })

    // Only update state if the item was found and updated
    if (itemFound) {
      set(basketAtom, { items: newItems })
    }

    // If item not found, we do nothing in an update operation
  }
})

/**
 * Atom to clear all items from the basket, resetting it to the initial empty state.
 */
const clearBasketAtom = atom(null, (_get, set) => {
  set(basketAtom, initialBasketState)
})

export { addItemAtom, removeItemAtom, updateItemQuantityAtom, clearBasketAtom }
